import type { AuthChangeEvent, Session, SupabaseClient } from '@supabase/supabase-js';

import { AuthError, type AuthClient, type AuthEvent, type AuthSession } from './auth-client';

type SupabaseAuth = SupabaseClient['auth'];

export function toSession(s: Session | null): AuthSession | null {
  return s ? { accessToken: s.access_token, userId: s.user.id, email: s.user.email ?? null } : null;
}

const EVENTS: readonly string[] = [
  'INITIAL_SESSION',
  'SIGNED_IN',
  'SIGNED_OUT',
  'TOKEN_REFRESHED',
  'PASSWORD_RECOVERY',
];

export function toEvent(e: AuthChangeEvent): AuthEvent {
  return EVENTS.includes(e) ? (e as AuthEvent) : 'OTHER';
}

function check(error: { message: string; code?: string } | null) {
  if (error) throw new AuthError(error.message, error.code ?? null);
}

export function createSupabaseAuthClient(auth: SupabaseAuth): AuthClient {
  return {
    async getSession() {
      const { data } = await auth.getSession();
      return toSession(data.session);
    },
    onAuthChange(callback) {
      const { data } = auth.onAuthStateChange((event, session) => callback(toEvent(event), toSession(session)));
      return () => data.subscription.unsubscribe();
    },
    async signInWithPassword(email, password) {
      check((await auth.signInWithPassword({ email, password })).error);
    },
    async signUp(email, password) {
      const { data, error } = await auth.signUp({ email, password });
      check(error);
      return { session: toSession(data.session) };
    },
    async signInWithOAuth(provider, redirectTo) {
      check((await auth.signInWithOAuth({ provider, options: { redirectTo } })).error);
    },
    async signOut() {
      await auth.signOut();
    },
    async refreshSession() {
      const { data, error } = await auth.refreshSession();
      return error ? null : toSession(data.session);
    },
    async requestPasswordReset(email, redirectTo) {
      check((await auth.resetPasswordForEmail(email, { redirectTo })).error);
    },
    async updatePassword(password) {
      check((await auth.updateUser({ password })).error);
    },
  };
}
